import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, ArrowRight, CheckCircle, Robot, Waveform, ShieldCheck } from "@phosphor-icons/react";

/**
 * AIFirstWaitlistCard — sits below the plan grid on the Billing page.
 *
 * Sells the upcoming V1.4 AI Interviewer capability + captures interest so
 * we can prioritise launch by real demand instead of guessing. Persists to
 * db.ai_first_waitlist via `POST /api/ai-first/waitlist`.
 *
 * Design notes:
 * - Uses the same ink + splash gradient the "featured" plan card uses so
 *   it reads as a natural extension of the pricing shelf, not an ad.
 * - Auto-fills workspace name + admin email from `props.workspace` / `props.user`
 *   so a single click submits ("Notify me at launch").
 * - Post-submit renders a "You're in" success state so users understand it worked.
 */
export default function AIFirstWaitlistCard({ workspace, user }) {
    const [email, setEmail] = useState(user?.email || "");
    const [teamSize, setTeamSize] = useState("6-20");
    const [monthlyEst, setMonthlyEst] = useState("");
    const [saving, setSaving] = useState(false);
    const [joined, setJoined] = useState(false);

    const submit = async () => {
        const trimmed = (email || "").trim();
        if (!trimmed.includes("@")) {
            toast.error("Enter a valid email so we can notify you at launch.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/ai-first/waitlist", {
                email: trimmed,
                workspace_name: workspace?.name || null,
                team_size: teamSize,
                interest_tier: "ai_first",
                monthly_screenings_estimate:
                    monthlyEst && !isNaN(Number(monthlyEst)) ? Number(monthlyEst) : null,
                source: "billing_page",
            });
            setJoined(true);
            toast.success(
                data?.already_on_list
                    ? "You're already on the AI-First waitlist."
                    : "You're on the list. We'll email you at launch."
            );
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Couldn't sign you up. Try again in a moment.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="ai-first-waitlist-card"
            className="mt-10 relative overflow-hidden border border-ink bg-ink text-white p-6 sm:p-8"
        >
            {/* Splash-cyan accent bar */}
            <span
                aria-hidden
                className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-splash via-cobalt to-splash"
            />

            <div className="grid md:grid-cols-2 gap-6 md:gap-10">
                {/* Left column — pitch */}
                <div>
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.24em] px-2.5 py-1 bg-gradient-to-r from-splash to-cobalt text-ink">
                        <Sparkle size={11} weight="fill" /> Coming soon
                    </span>
                    <h3 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-4">
                        AI-First plan.
                    </h3>
                    <p className="text-sm text-white/70 mt-2 leading-relaxed">
                        Let our AI take the entire screening round. It interviews the candidate,
                        scores them against your JD rubric, auto-shortlists for the next round —
                        and you only meet the top 20%.
                    </p>

                    <ul className="mt-5 space-y-2.5">
                        <li className="flex items-start gap-2.5 text-sm">
                            <Robot size={16} weight="bold" className="text-splash mt-0.5 shrink-0" />
                            <span className="text-white/90">
                                <strong>Async AI video interview</strong> — 6–8 questions, auto-graded, transcript + score in your dashboard
                            </span>
                        </li>
                        <li className="flex items-start gap-2.5 text-sm">
                            <Waveform size={16} weight="bold" className="text-splash mt-0.5 shrink-0" />
                            <span className="text-white/90">
                                <strong>Voice AI interviewer (premium)</strong> — real-time conversation, feels like a junior recruiter
                            </span>
                        </li>
                        <li className="flex items-start gap-2.5 text-sm">
                            <ShieldCheck size={16} weight="bold" className="text-splash mt-0.5 shrink-0" />
                            <span className="text-white/90">
                                <strong>DPDPA-compliant</strong> — consent, audit trail, recruiter-final-approval — never fully autonomous
                            </span>
                        </li>
                    </ul>

                    <p className="text-[11px] text-white/50 mt-5 leading-relaxed">
                        Waitlist members get <strong className="text-white">early access + a launch discount</strong>.
                    </p>
                </div>

                {/* Right column — form or success */}
                <div className="bg-white/5 border border-white/10 p-5 sm:p-6 flex flex-col">
                    {joined ? (
                        <div
                            data-testid="ai-first-waitlist-success"
                            className="flex flex-col items-center justify-center text-center h-full min-h-[220px]"
                        >
                            <CheckCircle size={44} weight="fill" className="text-splash" />
                            <p className="font-heading font-black text-lg tracking-tight text-white mt-3">
                                You're on the list.
                            </p>
                            <p className="text-xs text-white/70 mt-2 leading-relaxed max-w-xs">
                                We'll email you when the AI-First plan launches — expect early access and a launch discount.
                            </p>
                        </div>
                    ) : (
                        <>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/50">
                                Join the waitlist
                            </p>
                            <p className="text-sm text-white/80 mt-1 mb-4">
                                Be first to try it. Zero commitment.
                            </p>

                            <div className="space-y-3">
                                <div>
                                    <label
                                        htmlFor="ai-first-waitlist-email"
                                        className="block text-[10px] font-bold uppercase tracking-wider text-white/60 mb-1.5"
                                    >
                                        Work email
                                    </label>
                                    <input
                                        id="ai-first-waitlist-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@agency.com"
                                        data-testid="ai-first-waitlist-email-input"
                                        className="w-full bg-white/10 border border-white/20 px-3 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:border-splash"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label
                                            htmlFor="ai-first-waitlist-team"
                                            className="block text-[10px] font-bold uppercase tracking-wider text-white/60 mb-1.5"
                                        >
                                            Team size
                                        </label>
                                        <select
                                            id="ai-first-waitlist-team"
                                            value={teamSize}
                                            onChange={(e) => setTeamSize(e.target.value)}
                                            data-testid="ai-first-waitlist-team-select"
                                            className="w-full bg-white/10 border border-white/20 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-splash"
                                        >
                                            <option value="1-5">1–5</option>
                                            <option value="6-20">6–20</option>
                                            <option value="21-50">21–50</option>
                                            <option value="50+">50+</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="ai-first-waitlist-est"
                                            className="block text-[10px] font-bold uppercase tracking-wider text-white/60 mb-1.5"
                                        >
                                            Interviews/mo
                                        </label>
                                        <input
                                            id="ai-first-waitlist-est"
                                            type="number"
                                            min="0"
                                            value={monthlyEst}
                                            onChange={(e) => setMonthlyEst(e.target.value)}
                                            placeholder="Optional"
                                            data-testid="ai-first-waitlist-est-input"
                                            className="w-full bg-white/10 border border-white/20 px-3 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:border-splash"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={submit}
                                    disabled={saving}
                                    data-testid="ai-first-waitlist-submit-btn"
                                    className="w-full mt-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-splash to-cobalt text-ink hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed px-5 py-3 text-xs font-black uppercase tracking-wider transition-opacity"
                                >
                                    {saving ? "Adding you…" : (
                                        <>
                                            Notify me at launch <ArrowRight size={13} weight="bold" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
